import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { Trash2 } from 'lucide-react';
import { CommitInput } from '../../ui/CommitInput';
import { toastStore } from '../../state/toasts';
import { templatesStore } from './store';

/** Workspace settings: the templates (decision U16) – rename, delete (editors and up). */
export function TemplatesSection({ wsId, canEdit }: { wsId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const s = useStore(templatesStore);
  const [confirm, setConfirm] = useState<string | null>(null);
  useEffect(() => {
    void templatesStore.getState().load(wsId, true);
  }, [wsId]);
  const list = s.ws === wsId ? s.list : [];
  const fail = () =>
    toastStore.getState().push({ kind: 'error', message: t('templates.saveFailed') });
  return (
    <section className="settings-section" aria-labelledby="ws-templates">
      <h2 id="ws-templates">{t('templates.title')}</h2>
      {list.length === 0 && <p className="muted">{t('templates.none')}</p>}
      {list.length > 0 && (
        <ul className="member-list" aria-label={t('templates.title')}>
          {list.map((tpl) => (
            <li key={tpl.id} className="member-row">
              {canEdit ? (
                <CommitInput
                  aria-label={t('templates.nameLabel')}
                  value={tpl.name}
                  maxLength={200}
                  onCommit={(v) => {
                    if (v.trim() !== '' && v.trim() !== tpl.name)
                      void templatesStore.getState().rename(tpl.id, v.trim()).catch(fail);
                  }}
                />
              ) : (
                <strong>{tpl.name}</strong>
              )}
              <span className="muted small">
                {t('templates.summary', { items: tpl.items, channels: tpl.channels })} ·{' '}
                {new Date(tpl.createdAt).toLocaleDateString(i18n.language)}
                {tpl.createdBy ? ` · ${tpl.createdBy}` : ''}
              </span>
              {canEdit &&
                (confirm === tpl.id ? (
                  <span className="row-actions">
                    <span className="small">
                      {t('templates.confirmDelete', { name: tpl.name })}
                    </span>
                    <button
                      type="button"
                      className="btn btn-small btn-danger"
                      onClick={() => {
                        setConfirm(null);
                        void templatesStore
                          .getState()
                          .remove(tpl.id)
                          .then(
                            () =>
                              toastStore
                                .getState()
                                .push({ kind: 'success', message: t('templates.deleted') }),
                            fail,
                          );
                      }}
                    >
                      {t('templates.delete')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => setConfirm(null)}
                    >
                      {t('common.cancel')}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`${t('templates.delete')}: ${tpl.name}`}
                    onClick={() => setConfirm(tpl.id)}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
